import { isNetworkRetryable } from '@/services/retry-policy';

/**
 * Normalized classification of an error thrown by apiService.fetchApi so callers
 * can decide how to recover. The transport layer (services/api-service.ts) already
 * retries 5xx/network on its own with exponential backoff; this classification is
 * used by the orchestration layer (services/association-save.ts) to decide
 * auth-recovery vs. surfacing a final failure, and to tag bug reports.
 *
 * Policy (as specified for the resilient-save flow):
 *  - 401 / UnauthorizedError → `auth-expired`  → try a silent token refresh, retry once
 *  - 403                     → `forbidden`     → try a silent token refresh, retry once,
 *                                                then treat as a real permission error
 *  - other 4xx               → `client`        → do NOT retry (bad input / not found / conflict)
 *  - 5xx                     → `server`        → retryable (transport already backed off 3×)
 *  - network failure         → `network`       → retryable
 *  - timeout                 → `timeout`       → retryable
 *  - anything else           → `unknown`
 */
export type ApiErrorKind =
	| 'auth-expired'
	| 'forbidden'
	| 'client'
	| 'server'
	| 'network'
	| 'timeout'
	| 'unknown';

export interface ClassifiedError {
	kind: ApiErrorKind;
	/** HTTP status when the error carries one (parsed from the thrown message). */
	status?: number;
	/** Human-readable message, safe to show or log. */
	message: string;
	/** True for transient classes the transport already retried (server/network/timeout). */
	retryable: boolean;
}

// fetchApi throws `Error('API error <status>: <body>')` for non-ok responses.
const API_ERROR_RE = /^API error (\d{3})\b/;

/**
 * What fetchApi throws when a non-ok response carried a JSON error body: the same message every
 * caller already matches on, PLUS the server's structured `code` and the parsed body, so a caller
 * can branch on `ACCOUNT_NUMBER_TAKEN` (and read what came with it) instead of string-matching.
 */
export class ApiResponseError extends Error {
	constructor(
		message: string,
		readonly status: number,
		readonly code: string | undefined,
		readonly body: Record<string, unknown>,
	) {
		super(message);
		this.name = 'ApiResponseError';
	}
}

/** The server's error `code`, when the error came from a JSON error body. */
export const apiErrorCode = (error: unknown): string | undefined =>
	error instanceof ApiResponseError ? error.code : undefined;

const getMessage = (error: unknown): string => {
	if (error instanceof Error) return error.message;
	if (typeof error === 'string') return error;
	return 'Unknown error';
};

/** True when the error is an UnauthorizedError without importing the auth-service class. */
const isUnauthorizedError = (error: unknown): boolean =>
	!!error && typeof error === 'object' && (error as { name?: string }).name === 'UnauthorizedError';

export const classifyApiError = (error: unknown): ClassifiedError => {
	const message = getMessage(error);

	// 401 surfaces either as UnauthorizedError (token refresh already failed in
	// transport) or as an `API error 401:` message — both mean re-auth.
	if (isUnauthorizedError(error)) {
		return { kind: 'auth-expired', status: 401, message, retryable: false };
	}

	const match = message.match(API_ERROR_RE);
	if (match) {
		const status = Number(match[1]);
		if (status === 401) return { kind: 'auth-expired', status, message, retryable: false };
		if (status === 403) return { kind: 'forbidden', status, message, retryable: false };
		if (status >= 500) return { kind: 'server', status, message, retryable: true };
		// fetchApi only throws `API error <status>` for non-ok responses, so any
		// remaining match is a 4xx the caller shouldn't retry.
		return { kind: 'client', status, message, retryable: false };
	}

	// No HTTP status — distinguish network/timeout (retryable) from unknown.
	if (isNetworkRetryable(error)) {
		const isTimeout = (error as { name?: string })?.name === 'FetchTimeoutError'
			|| /timed out|timeout/i.test(message);
		return { kind: isTimeout ? 'timeout' : 'network', message, retryable: true };
	}

	return { kind: 'unknown', message, retryable: false };
};

/** Auth classes worth a one-shot silent token refresh + retry. */
export const isAuthRecoverable = (kind: ApiErrorKind): boolean =>
	kind === 'auth-expired' || kind === 'forbidden';

/**
 * A message safe to show a user: strips the transport's `API error <status>: `
 * prefix (and a leading server JSON envelope, if the body wasn't parsed) so a
 * handler's helpful 4xx message reads cleanly in a Snackbar/toast. Falls back
 * to the original message, then to `fallback`.
 */
export const friendlyApiMessage = (error: unknown, fallback = 'Something went wrong.'): string => {
	const raw = getMessage(error).replace(API_ERROR_RE, '').replace(/^:\s*/, '').trim();
	if (!raw) return fallback;
	// If the body came through as raw JSON (`{"type":"error","message":"…"}`),
	// surface just the message field.
	if (raw.startsWith('{')) {
		try {
			const parsed = JSON.parse(raw) as { message?: string };
			if (parsed.message) return parsed.message;
		} catch { /* not JSON — use raw */ }
	}
	return raw;
};
